'use client'

import { useQuery } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import { useCallback, useMemo, useState } from 'react'

import {
  computeTradeStats,
  type FeeRates,
  type TradeStats,
} from '@/features/home/lib/home-trade-stats'
import { resolveFeeTier } from '@/features/order/order-form/lib/resolve-fee-tier'
import { useMultiExchangeInstrumentMap } from '@/hooks/use-multi-exchange-instrument-map'
import { getInstrumentMapForExchange } from '@/lib/utils/exchange-instrument-map'
import { fetchTradeHistory } from '@/service/account-history.api'
import { useActiveAccountsScope } from '@/service/account.hook'
import { accountFeesByIdAtom } from '@/state/account'
import type { Trade } from '@/types/generated/common/trading'
import type { Instrument } from '@/types/generated/common/types'

const DAY_MS = 86_400_000
/** Always fetch the longest window shown; the shorter one is a slice of it. */
const HISTORY_DAYS = 30
const PAGE_SIZE = 500
/** Stops a very active account from paging forever (≈ 20k fills). */
const MAX_PAGES = 40

/**
 * Every fill of the last 30 days across all active accounts, paged back with the same
 * oldest-fill cursor the Trades table uses (the store behind that table is capped and filtered,
 * so it can't stand in for a volume total).
 */
async function fetchRecentTrades(accountIds: string[], from: number): Promise<Trade[]> {
  const byId = new Map<string, Trade>()
  let to: string | undefined
  for (let page = 0; page < MAX_PAGES; page++) {
    const { trades = [] } = await fetchTradeHistory({
      accountId: '',
      accountIds,
      symbols: [],
      currency: [],
      from: String(from),
      to,
      limit: PAGE_SIZE,
    })
    let oldest = Infinity
    let added = 0
    for (const trade of trades) {
      const at = Number(trade.executedAt)
      if (Number.isFinite(at) && at > 0 && at < oldest) oldest = at
      if (!byId.has(trade.id)) {
        byId.set(trade.id, trade)
        added += 1
      }
    }
    if (trades.length === 0 || added === 0 || oldest === Infinity || oldest <= from) break
    to = String(oldest)
  }
  return [...byId.values()]
}

/**
 * The last 30 days of fills across all active accounts (one query shared by the trading panels
 * and the trading review), with each fill's instrument and the account's own fee rates.
 */
export function useHomeTradeHistory() {
  const { accountIds, isResolved, hasAccounts } = useActiveAccountsScope()
  const [now] = useState(() => Date.now())
  const instrumentMap = useMultiExchangeInstrumentMap()
  const feesByAccount = useAtomValue(accountFeesByIdAtom)

  const query = useQuery({
    queryKey: ['home-trade-history', accountIds.join(','), Math.floor(now / DAY_MS)],
    queryFn: () => fetchRecentTrades(accountIds, now - HISTORY_DAYS * DAY_MS),
    enabled: isResolved && hasAccounts,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  })

  const instrumentOf = useCallback(
    (trade: Trade) => getInstrumentMapForExchange(instrumentMap, trade.exchange).get(trade.symbol),
    [instrumentMap]
  )
  const feeRatesOf = useCallback(
    (trade: Trade, instrument: Instrument | undefined): FeeRates | null => {
      const tier = instrument
        ? resolveFeeTier(instrument, feesByAccount[trade.accountId] ?? null)
        : null
      const maker = Number(tier?.makerFee ?? instrument?.makerCommission)
      const taker = Number(tier?.takerFee ?? instrument?.takerCommission)
      return Number.isFinite(maker) && Number.isFinite(taker) ? { maker, taker } : null
    },
    [feesByAccount]
  )

  return {
    trades: query.data ?? null,
    now,
    instrumentOf,
    feeRatesOf,
    isPending: !isResolved || (hasAccounts && query.isPending),
    hasAccounts,
  }
}

/**
 * The user's trading volume, maker share, fees and the OrderX savings estimates over the last
 * `days`, priced with each instrument's metadata and the account's own fee tier.
 */
export function useHomeTradeStats(days: 7 | 30): {
  stats: TradeStats | null
  isPending: boolean
  hasAccounts: boolean
} {
  const { trades, now, instrumentOf, feeRatesOf, isPending, hasAccounts } = useHomeTradeHistory()
  const stats = useMemo(() => {
    if (!trades) return null
    return computeTradeStats({
      trades,
      instrumentOf,
      feeRatesOf,
      from: now - days * DAY_MS,
      now,
      days,
    })
  }, [trades, instrumentOf, feeRatesOf, now, days])

  return { stats, isPending, hasAccounts }
}
